import { useMemo } from "react"
import type { Message, Session } from "../../lib/opencodeTypes"
import type { SessionContext } from "../../lib/sessionContext"
import { contextBreakdown, type BreakdownKey } from "../../lib/contextBreakdown"

// The session-details side panel — the web twin of the desktop's Context tab
// (agent/packages/app/src/components/session/session-context-tab.tsx): the
// same stacked label-over-value pairs in two columns, the same breakdown bar,
// the same raw-messages list with an export. One deliberate difference: the
// provider row says Moderniser, the product — which vendor serves it is not a
// customer's to know (gateway/src/product.ts).

const EM_DASH = "—"

const number = (value: number | undefined) =>
  value === undefined || value === null ? EM_DASH : value.toLocaleString()

const when = (value: string | number | undefined) => {
  if (!value) return EM_DASH
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? EM_DASH
    : date.toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
}

// Roles from the palette, not new colours. index.css is explicit that no
// component picks a hex or a Tailwind palette colour.
const SEGMENT: Record<BreakdownKey, { bar: string; dot: string; label: string }> = {
  system: { bar: "bg-faint", dot: "bg-faint", label: "System" },
  user: { bar: "bg-accent", dot: "bg-accent", label: "You" },
  assistant: { bar: "bg-success", dot: "bg-success", label: "Assistant" },
  tool: { bar: "bg-warning", dot: "bg-warning", label: "Tools" },
  other: { bar: "bg-border-strong", dot: "bg-border-strong", label: "Other" },
}

// Desktop-style pair: the label sits above the value.
function Pair({ label, value, title }: { label: string; value: string; title?: string }) {
  return (
    <div className="min-w-0" title={title}>
      <div className="text-xs text-muted">{label}</div>
      <div className="truncate font-mono text-xs tabular-nums text-foreground">{value}</div>
    </div>
  )
}

function SectionTitle({ children }: { children: string }) {
  return <div className="mb-2 mt-6 text-xs font-medium text-muted">{children}</div>
}

export function ContextPanel({
  session,
  context,
  messages,
  cost,
  billedTo,
  showReasoning,
  onShowReasoningChange,
  onExport,
}: {
  session: Session
  context: SessionContext | undefined
  messages: Message[]
  cost: string | undefined
  billedTo: string | undefined
  showReasoning: boolean
  onShowReasoningChange: (show: boolean) => void
  onExport: () => void
}) {
  const counts = useMemo(() => {
    let user = 0
    let assistant = 0
    for (const message of messages) {
      if (message.type === "user") user++
      else if (message.type === "assistant") assistant++
    }
    return { user, assistant, all: messages.length }
  }, [messages])

  // Estimated, and scaled to the input count the provider actually reported —
  // see lib/contextBreakdown.ts for why this cannot be measured directly.
  const segments = useMemo(
    () => (context ? contextBreakdown(messages, context.tokens.input) : []),
    [messages, context],
  )

  const tokens = context?.tokens
  const raw = useMemo(
    () => messages.filter((message) => message.type === "user" || message.type === "assistant"),
    [messages],
  )

  return (
    <div className="h-full w-full overflow-y-auto bg-surface px-4 pb-6 pt-3">
      <div className="grid grid-cols-2 gap-x-6 gap-y-3">
        <Pair label="Session" value={session.title || "Untitled"} />
        <Pair label="Messages" value={number(counts.all)} />
        <Pair label="Provider" value="Moderniser" />
        <Pair label="Model" value={context?.modelLabel ?? EM_DASH} />
        <Pair
          label="Context limit"
          value={number(context?.limit)}
          title={
            context?.limit
              ? undefined
              : "Nobody has set this model's context window, so usage cannot be shown as a percentage. Platform → Models."
          }
        />
        <Pair
          label="Total tokens"
          value={number(context?.total)}
          title="The most recent turn, not a sum of the session — a turn's input already covers the whole conversation."
        />
        <Pair label="Usage" value={context?.usage == null ? EM_DASH : `${context.usage}%`} />
        <Pair label="Input tokens" value={number(tokens?.input)} />
        <Pair label="Output tokens" value={number(tokens?.output)} />
        <Pair label="Reasoning tokens" value={number(tokens?.reasoning)} />
        <Pair
          label="Cache (read / write)"
          value={`${number(tokens?.cache.read)} / ${number(tokens?.cache.write)}`}
        />
        <Pair label="Your messages" value={number(counts.user)} />
        <Pair label="Assistant messages" value={number(counts.assistant)} />
        <Pair
          label="Session cost"
          value={cost ?? EM_DASH}
          title={`Charged to ${billedTo ?? "the workspace's company"} at its rate, summed across every turn of this session.`}
        />
        <Pair
          label="Billed to"
          value={billedTo ?? EM_DASH}
          title="The company this workspace belongs to. Its credits pay for this session, whatever your home company is."
        />
        <Pair label="Created" value={when(session.time?.created)} />
        <Pair label="Last activity" value={when(session.time?.updated)} />
      </div>

      <SectionTitle>Chat display</SectionTitle>
      <div className="flex items-center justify-between gap-4 rounded-md border border-border bg-surface-muted px-3 py-2.5">
        <div className="min-w-0">
          <div className="text-xs font-medium text-foreground">Show reasoning</div>
          <div className="mt-0.5 text-xs text-muted">
            Display the agent's reasoning blocks in the conversation.
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={showReasoning}
          aria-label="Show reasoning"
          onClick={() => onShowReasoningChange(!showReasoning)}
          className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${
            showReasoning ? "bg-accent" : "bg-border-strong"
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 size-4 rounded-full shadow-sm transition-transform ${
              showReasoning
                ? "translate-x-4 bg-accent-foreground"
                : "translate-x-0 bg-surface"
            }`}
          />
        </button>
      </div>

      {context && segments.length > 0 && (
        <>
          <SectionTitle>Context breakdown</SectionTitle>
          <div className="flex h-2 w-full overflow-hidden rounded-full bg-surface-muted" aria-hidden>
            {segments.map((segment) => (
              <span
                key={segment.key}
                className={SEGMENT[segment.key].bar}
                style={{ width: `${segment.width}%` }}
              />
            ))}
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
            {segments.map((segment) => (
              <span key={segment.key} className="inline-flex items-center gap-1.5 text-muted">
                <span className={`size-2 rounded-full ${SEGMENT[segment.key].dot}`} aria-hidden />
                {SEGMENT[segment.key].label}
                <span className="font-mono tabular-nums text-faint">{segment.percent}%</span>
              </span>
            ))}
          </div>
          <p className="mt-2 text-xs text-faint">
            Estimated from the text of the conversation and scaled to the {number(tokens?.input)} prompt
            tokens the provider reported. “Other” is what we cannot see — the system prompt, the tool
            definitions, the agent's own instructions.
          </p>
        </>
      )}

      {!context && (
        <p className="mt-6 text-xs text-muted">
          Nothing has been measured yet — send a message and this fills in once the reply finishes.
        </p>
      )}

      <div className="mb-2 mt-6 flex items-center justify-between">
        <span className="text-xs font-medium text-muted">Raw messages</span>
        <button
          type="button"
          onClick={onExport}
          className="rounded-md px-1.5 py-0.5 text-xs text-muted transition-colors hover:bg-surface-muted hover:text-foreground"
        >
          ↓ Export session
        </button>
      </div>
      <div className="overflow-hidden rounded-md border border-border">
        {raw.length === 0 && <div className="px-3 py-2 text-xs text-muted">No messages yet.</div>}
        {raw.map((message) => (
          <div
            key={message.id}
            className="flex items-baseline justify-between gap-3 border-b border-border/60 px-3 py-1.5 last:border-b-0"
          >
            <span className="min-w-0 truncate font-mono text-xs text-foreground">
              <span className="text-muted">{message.type}</span> · {message.id}
            </span>
            <span className="shrink-0 font-mono text-[11px] tabular-nums text-faint">
              {when("time" in message && message.time && typeof message.time === "object" ? message.time.created : undefined)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
